import { Provider } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { sortBy } from 'lodash';
import { filter, map, tap } from 'rxjs/operators';
import { INITIALIZER } from '../../initialize';

/**
 * Determine the canonical title for a URL based on the data in its route.
 *
 * Example route data for module.ts:
 * ```
 * data: {
 *   title: 'Estimates',
 *   queryParamTitles: [
 *     {
 *       condition: 'action=ez-estimation-modal',
 *       title: 'Estimate editor',
 *     },
 *     {
 *       condition: 'action=simple-estimate-view',
 *       title: 'Estimate - ',
 *       appendTitleTextFrom: estimationStatusQueryParam,
 *     },
 *   ],
 * },
 * ```
 */
export function pageTitleFromRouteData(
  url: string,
  route: ActivatedRouteSnapshot | null,
): string | null {
  // Query params e.g. [ 'action=info' ]
  const urlQueryParams = (url.split('?')[1] ?? '').split('&');
  const urlQueryParamsSet = new Set(urlQueryParams);
  // Query params map e.g. { 'action': 'info' }
  const urlQueryParamsMap = Object.fromEntries(
    urlQueryParams.map(urlQueryParam => [
      urlQueryParam.split('=')[0],
      urlQueryParam.split('=')[1],
    ]),
  );

  let title = null;
  traverseRoute: for (; route != null; route = route.firstChild) {
    const queryParamTitles = (route.routeConfig?.data?.queryParamTitles ??
      []) as {
      condition?: string;
      title: string;
      appendToTitleFrom?: string;
    }[];
    // Check if URL matches any queryParamTitles
    checkQueryParamTitles: for (const {
      condition,
      title: queryParamTitle,
      appendToTitleFrom,
    } of sortBy(queryParamTitles, queryParam => !queryParam.condition)) {
      if (
        !condition ||
        condition
          .split('&')
          .every(queryParam => urlQueryParamsSet.has(queryParam))
      ) {
        // Yes, we've matched to this queryParamTitle
        // If there's an appendToTitleFrom, the param needs to have a value
        if (appendToTitleFrom && !urlQueryParamsMap[appendToTitleFrom]) {
          continue checkQueryParamTitles;
        }
        title = queryParamTitle;
        if (appendToTitleFrom) {
          title += decodeURIComponent(urlQueryParamsMap[appendToTitleFrom]);
        }
        continue traverseRoute;
      }
    }
    // If there are query param titles, but we didn't match any, don't change
    // the title until one loads
    if (queryParamTitles.length > 0 && !route.routeConfig?.data?.title) {
      return null;
    }
    // Otherwise, use regular title if available
    if (route.routeConfig?.data?.title) {
      title = route.routeConfig?.data?.title;
      continue traverseRoute;
    }
  }
  // Return most specific title found
  return title;
}

export const TITLE_INITIALIZER_DEPS: Provider[] = [Router, Title];

export function titleInitializer(router: Router, title: Title) {
  return router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(event => event as NavigationEnd),
    map(event =>
      pageTitleFromRouteData(event.url, router.routerState.snapshot.root),
    ),
    tap(value => {
      if (value) title.setTitle(value);
    }),
  );
}

export const TITLE_PROVIDERS: Provider[] = [
  {
    deps: TITLE_INITIALIZER_DEPS,
    multi: true,
    provide: INITIALIZER,
    useFactory: titleInitializer,
  },
];
